import { FormControlLabel } from '@material-ui/core'
import React from 'react'
import { collect } from '../lib/utils'
import PageStatus from '../PageStatus'
import pageStatusStyles from '../PageStatus.module.css'
import State from '../State'
import Student from '../Student'
import baseStyles from './Base.module.css'
import styles from './ComptesAcces.module.css'
import CompteWarning from '../CompteWarning'

export default ({ path }) => {
  const { student } = Student.useContainer()

  return (
    <div className={baseStyles.root}>
      <h2>Comptes d'accès</h2>
      {(!student.passwordHasBeenModified || !student.eduIDIsLinked || !student.entraMfa || !student.mfaConfigured) && (
        <CompteWarning></CompteWarning>
      )}

      <PageStatus path={path}>
        <p className={pageStatusStyles.title}>Statut des actions à entreprendre</p>
        <div className={styles['control-labels']}>
          <FormControlLabel
            control={<State done={student.passwordHasBeenModified} />}
            label={
              student.passwordHasBeenModified
                ? `Mot de passe d'accès de la HEIG-VD modifié`
                : `Mot de passe d'accès de la HEIG-VD pas encore modifié`
            }
            className={collect(pageStatusStyles.label, pageStatusStyles.noPointer)}
          />
          <FormControlLabel
            control={<State done={student.eduIDIsLinked} />}
            label={
              student.eduIDIsLinked
                ? 'Compte edu-ID privé correctement lié à la HES-SO'
                : 'Compte edu-ID privé pas encore lié à la HES-SO'
            }
            className={collect(pageStatusStyles.label, pageStatusStyles.noPointer)}
          />
          <FormControlLabel
            control={<State done={student.entraMfa} />}
            label="MFA configuré sur le compte Microsoft"
            className={collect(pageStatusStyles.label, pageStatusStyles.noPointer)}
          />
          <FormControlLabel
            control={<State done={student.mfaConfigured} />}
            label={
              student.mfaConfigured
                ? 'MFA configuré sur le compte SWITCH edu-ID'
                : 'MFA pas encore configuré sur le compte SWITCH edu-ID'
            }
            className={collect(pageStatusStyles.label, pageStatusStyles.noPointer)}
          />
          <hr></hr>
          <div>⚠️ Le contrôle de l'activation du MFA sur le compte Microsoft est effectué tous les jours à 02h00.</div>
        </div>
      </PageStatus>
      <section className={styles.section}>
        <span className={styles.eyebrow}>Étapes</span>
        <h2>Ce que vous devez faire</h2>

        <div className={styles['step-pair']}>
          <article className={styles.step}>
            <div className={styles['step-number']}>1</div>
            <h3>Initialiser votre compte en changeant votre mot de passe</h3>
            <p>
              Modifiez immédiatement le mot de passe initial de votre compte institutionnel.
            </p>
            <p>
              <a href="https://heig.ch/AAI-login" target="_blank" rel="noopener noreferrer">
                Modifier le mot de passe de mon compte
              </a>
            </p>
          </article>

          <article className={styles.step}>
            <div className={styles['step-number']}>2</div>
            <h3>Créer ou relier votre compte SWITCH edu-ID</h3>
            <p>
              Créez votre compte SWITCH edu-ID avec votre <strong>adresse e-mail privée</strong>,
              puis reliez-le à la HES-SO.
            </p>
            <p>
              <a href="https://www.hes-so.ch/edu-id" target="_blank" rel="noopener noreferrer">
                Procédure SWITCH edu-ID
              </a>
            </p>
          </article>
        </div>

        <div className={styles['video-embed']}>
          <a
            href="https://www.hes-so.ch/fileadmin/documents/HES-SO/Documents_HES-SO/pdf/si/Comment_acceder_aux_ressources_numeriques_de_la_HES-FR.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Comment accéder aux ressources numériques de la HES-SO (PDF)
          </a>
        </div>

        <div className={styles['pair-separator']} />

        <div className={styles['step-pair']}>
          <article className={styles.step}>
            <div className={styles['step-number']}>3</div>
            <h3>Configurer le MFA Microsoft</h3>
            <p>
              Activez l'authentification multifacteur pour vos accès Microsoft 365
              (Outlook, Teams, Office, etc.).
            </p>
            <p>
              <a
                href="https://res.cloudinary.com/dxvyngy9l/admission/comptes-acces/Procedure_de_configuration_MFA.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Procédure d'activation du MFA Microsoft
              </a>
            </p>
          </article>

          <article className={styles.step}>
            <div className={styles['step-number']}>4</div>
            <h3>Configurer le MFA de votre compte SWITCH edu-ID / HES-SO</h3>
            <p>
              Configurez également l'authentification multifacteur pour ce compte.
            </p>
          </article>
          <article className={styles.step}>
            <div className={styles['step-number']}>5</div>
            <h3>Demande d’accès au VPN International (facultatif)</h3>
            <p>
              Certains liens d’informations redirigent sur notre site interne (< a href="https://intra.heig-vd.ch" target="_blank">https://intra.heig-vd.ch</a>). Si vous y accédez depuis une zone géographique hors de l’Europe, vous devez faire une demande à l’adresse <a href="mailto:helpdesk@heig-vd.ch">helpdesk@heig-vd.ch</a> afin qu’un accès VPN International vous soit octroyé pour une durée de 60 jours.
            </p>
          </article>
        </div>
      </section>

      <section className={styles.section}>
        <span className={styles.eyebrow}>Repères utiles</span>
        <h2>Comprendre vos accès</h2>
        <p className={styles['mini-note']}>
          Vous avez <strong>2 comptes à connaître</strong>. L'adresse e-mail HEIG-VD est uniquement un alias de votre compte Microsoft.
        </p>

        <div className={styles.cards}>
          <article className={styles.card}>
            <span className={styles['card-label']}>Compte 1</span>
            <h3>Compte institutionnel HES-SO / HEIG-VD</h3>
            <p>
              Le compte institutionnel se décline en <strong>2 identités</strong> selon le service utilisé et d'une
              <strong> adresse e-mail.</strong>
            </p>
            <p><strong>Pour Microsoft 365 :</strong> <code className={styles.code}>8.8@hes-so.ch</code></p>
            <ul className={styles['usage-list']}>
              <li>Microsoft 365 : Outlook, Teams, Office</li>
              <li>Applications Web qui nécessitent de se connecter via le compte HES-SO</li>
            </ul>
            <p><strong>Pour l'intranet et certains services internes :</strong> <code className={styles.code}>8.8</code> ou <code className={styles.code}>EINET\8.8</code></p>
            <ul className={styles['usage-list']}>
              <li>Intranet</li>
              <li>Serveurs de fichiers</li>
              <li>Imprimantes</li>
              <li>Autres services internes</li>
            </ul>
            <p><strong>Adresse e-mail HEIG-VD :</strong> <code className={styles.code}>votre-prenom.votre-nom@heig-vd.ch</code></p>
            <p className={styles['mini-note']}>
              Cette adresse e-mail est un alias de votre compte Microsoft <code className={styles.code}>8.8@hes-so.ch</code>. Elle ne doit pas être utilisée comme identifiant de connexion sur les services Microsoft 365.
            </p>
            <p className={styles['mini-note']}>
              Elle vous permet d'envoyer et recevoir des e-mails dans une forme d'adresse classique et qui respecte la composition complète de votre prénom et nom.
            </p>
            <p className={styles['mini-note']}>
              L'adresse e-mail n'est donc pas tronquée à 8.8 caractères.
            </p>
          </article>

          <article className={styles.card}>
            <span className={styles['card-label']}>Compte 2</span>
            <h3>Compte SWITCH edu-ID</h3>
            <p>
              Ce compte est personnel et lié à votre <strong>adresse e-mail privée</strong>.
            </p>
            <p>
              Il possède son <strong>propre mot de passe</strong> et sert pour certains services HES-SO.
            </p>
            <p className={styles['mini-note']}>
              Il continue d'exister après la fin de vos études.
            </p>
          </article>
        </div>

        <div className={styles['callout-info']}>
          <strong>En résumé :</strong><br />
          • Pour Microsoft 365 → utilisez <code className={styles.code}>8.8@hes-so.ch</code><br />
          • Pour l'intranet, les serveurs de fichiers, les imprimantes et certains autres services internes → utilisez <code className={styles.code}>8.8</code> ou <code className={styles.code}>EINET\8.8</code><br />
          • Pour SWITCH edu-ID → utilisez votre <strong>adresse e-mail privée</strong> et non votre adresse e-mail @heig-vd.ch
        </div>
      </section>

      <section className={styles.section}>
        <span className={styles.eyebrow}>Explication</span>
        <h2>Que signifie "8.8" ?</h2>
        <p>
          Votre identifiant HES-SO est construit avec un maximum de
          <strong> 8 caractères pour le prénom</strong> et <strong>8 caractères pour le nom</strong>.
        </p>
        <p>
          <strong>Exemple :</strong><br />
          <code className={styles.code}>Alexandria Montgomerystone</code> devient<br />
          <code className={styles.code}>alexandr.montgome@hes-so.ch</code>
        </p>
        <p className={styles['mini-note']}>
          Selon le service, cet identifiant peut apparaître sous différentes formes :
          {' '}<code className={styles.code}>8.8@hes-so.ch</code>, <code className={styles.code}>8.8</code> ou <code className={styles.code}>EINET\8.8</code>.
        </p>
      </section>

      <section className={styles.section}>
        <span className={styles.eyebrow}>FAQ</span>
        <h2>Questions fréquentes</h2>

        <div className={styles.faq}>
          <details className={styles['faq-item']}>
            <summary>Je n'arrive pas à me connecter à Microsoft 365</summary>
            <p>
              Vérifiez que vous utilisez bien votre identifiant
              {' '}<code className={styles.code}>8.8@hes-so.ch</code> et non votre adresse
              {' '}<code className={styles.code}>prenom-complet.nom-complet@heig-vd.ch</code>.
            </p>
          </details>

          <details className={styles['faq-item']}>
            <summary>Quel identifiant utiliser pour l'intranet ou les services internes ?</summary>
            <p>
              Pour l'intranet, les serveurs de fichiers, les imprimantes et certains autres services internes,
              utilisez <code className={styles.code}>8.8</code> ou <code className={styles.code}>EINET\8.8</code> selon ce qui est demandé à l'écran.
            </p>
          </details>

          <details className={styles['faq-item']}>
            <summary>Pourquoi ai-je aussi un compte SWITCH edu-ID ?</summary>
            <p>
              Parce que certains services HES-SO utilisent SWITCH edu-ID et non le compte Microsoft.
              Ce compte est personnel, lié à votre adresse e-mail privée, et possède son propre mot de passe.
            </p>
          </details>

          <details className={styles['faq-item']}>
            <summary>J'ai un problème lors d'une étape</summary>
            <p>
              Si vous rencontrez une difficulté, contactez le helpdesk.
            </p>
          </details>
          <details className={styles['faq-item']}>
            <summary>Comment je consulte mes e-mails ?</summary>
            <p>Option 1 (Mode Web) : Depuis n’importe quelle navigateur, avec une connexion internet vous pouvez vous rendre sur <a href="https://webmail.heig-vd.ch">https://webmail.heig-vd.ch</a> et vous serez redirigés sur Outlook Online, basé sur les services Microsoft 365 de la HES-SO (connexion avec votre <code className={styles.code}>8.8@hes-so.ch</code> + votre mot de passe)</p>

            <p>Option 2 (Application) : Depuis votre ordinateur ou appareil mobile, vous pouvez configurer votre adresse de messagerie sur un client. La HEIG-VD recommande l’utilisation exclusive d’Outlook afin d’assurer la compatibilité. Vous trouverez les procédures pour Windows, macOS, Android et iOS sur notre intranet <a href="https://go.heig-vd.ch/si">https://go.heig-vd.ch/si</a></p>
          </details>
        </div>
      </section>

      <section className={styles.section}>
        <span className={styles.eyebrow}>Support</span>
        <h2>Besoin d'aide ?</h2>
        <p>
          Si vous rencontrez un problème à l'une de ces étapes, prenez contact avec le helpdesk.
        </p>

        <div className={styles.actions}>
          <a className={styles.button} href="https://heig-vd.ch/campus/service-informatique" target="_blank" rel="noopener noreferrer">
            024 557 63 33 - helpdesk@heig-vd.ch
          </a>
          <a className={styles['button-secondary']} href="https://my.hes-so.ch/pwm/private/login" target="_blank" rel="noopener noreferrer">
            Gestion du mot de passe HES-SO
          </a>
        </div>
      </section>
    </div>
  )
}
